import { useState, useEffect, useCallback } from "react";
import { C, fmt } from "../styles/tokens";
import { Card, StatCard, Btn, Input, Select, Spinner, Empty } from "../components/ui";
import { reportsService } from "../api/services";

/* ══════════════════════════════════════════════════════════
   REPORTS SCREEN
   Toutes les statistiques sont calculées côté serveur sur la
   période sélectionnée (mois en cours par défaut, ou plage
   personnalisée début/fin). L'export respecte la même période.
   ══════════════════════════════════════════════════════════ */

const PERIODS = [
  { value: "month",  label: "Mois en cours" },
  { value: "7",      label: "7 derniers jours" },
  { value: "30",     label: "30 derniers jours" },
  { value: "90",     label: "3 derniers mois" },
  { value: "365",    label: "12 derniers mois" },
  { value: "custom", label: "Période personnalisée" },
];

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const todayStr = () => new Date().toISOString().slice(0, 10);

// Construit les params de période pour l'API (stats + export)
const buildPeriodParams = (period, dateFrom, dateTo) => {
  if (period === "custom") {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }
  return { period };
};

const ReportsScreen = ({ toast, role }) => {
  // Le Gérant est restreint au rapport journalier (aujourd'hui) : pas de sélecteur.
  const isGerant = (role || "").toLowerCase().startsWith("gérant") ||
                   (role || "").toLowerCase().startsWith("gerant");

  // Filtres de période
  const [period, setPeriod] = useState(isGerant ? "custom" : "month");
  const [dateFrom, setDateFrom] = useState(isGerant ? todayStr() : firstOfMonth());
  const [dateTo, setDateTo] = useState(todayStr());

  // Données serveur
  const [kpi, setKpi] = useState(null);
  const [orders, setOrders] = useState(null);
  const [stock, setStock] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [exporting, setExporting] = useState(null); // type en cours d'export

  const load = useCallback(() => {
    if (period === "custom" && !dateFrom && !dateTo) return;
    setFetching(true);
    const p = buildPeriodParams(period, dateFrom, dateTo);
    Promise.all([
      reportsService.kpi(p).then(d => { if (d) setKpi(d); }),
      reportsService.orders(p).then(d => { if (d) setOrders(d); }),
      reportsService.stock(p).then(d => { if (d) setStock(d); }),
    ]).catch(() => {}).finally(() => setFetching(false));
  }, [period, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const doExport = async (type, format) => {
    setExporting(`${type}-${format}`);
    try {
      await reportsService.export(type, format, buildPeriodParams(period, dateFrom, dateTo));
      toast.success("Export généré", "Le fichier a été téléchargé");
    } catch (err) {
      toast.warning("Export", err.message || "Échec de l'export");
    } finally {
      setExporting(null);
    }
  };

  const periodLabel = kpi?.date_from ? `${kpi.date_from} → ${kpi.date_to}` : "Sur la période";
  const topPlats = orders?.top_plats ?? [];
  const alertes = stock?.en_alerte ?? [];

  return (
    <div style={{ padding: 28, overflowY: "auto", flex: 1 }}>
      {/* Barre de filtres de période */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 22, flexWrap: "wrap" }}>
        {isGerant ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase" }}>Période</label>
            <div style={{ background: C.bg3, border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 8, padding: "9px 14px", color: C.gold, fontSize: 13, fontWeight: 600 }}>
              Rapport du jour · {todayStr()}
            </div>
          </div>
        ) : (
          <>
            <Select label="Période" value={period} onChange={setPeriod} options={PERIODS} style={{ minWidth: 160 }}/>
            {period === "custom" && (
              <>
                <Input label="Début" type="date" value={dateFrom} onChange={setDateFrom} style={{ minWidth: 150 }}/>
                <Input label="Fin" type="date" value={dateTo} onChange={setDateTo} style={{ minWidth: 150 }}/>
              </>
            )}
          </>
        )}
        <div style={{ flex: 1 }}/>
        <span style={{ fontSize: 11, color: C.muted, alignSelf: "center" }}>{periodLabel}</span>
      </div>

      {fetching ? <Spinner/> : (
        <>
          {/* Synthèse financière : Chiffre d'affaires · Dépenses · Résultat */}
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 10 }}>
            Synthèse financière
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 24 }}>
            <Card style={{ padding: 20, borderLeft: `3px solid ${C.success}` }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Chiffre d'affaires</div>
              <div className="serif" style={{ fontSize: 26, fontWeight: 700, color: C.success, marginTop: 6 }}>{fmt(kpi?.ca_total ?? 0)}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{kpi?.nb_factures ?? 0} facture(s) · moy/jour {fmt(Math.round(kpi?.ca_moyen_jour ?? 0))}</div>
            </Card>
            <Card style={{ padding: 20, borderLeft: `3px solid ${C.danger}` }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Dépenses</div>
              <div className="serif" style={{ fontSize: 26, fontWeight: 700, color: C.danger, marginTop: 6 }}>{fmt(kpi?.depenses_total ?? 0)}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{kpi?.nb_depenses ?? 0} dépense(s)</div>
            </Card>
            <Card style={{ padding: 20, borderLeft: `3px solid ${(kpi?.resultat ?? 0) >= 0 ? C.gold : C.danger}` }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Résultat</div>
              <div className="serif" style={{ fontSize: 26, fontWeight: 700, color: (kpi?.resultat ?? 0) >= 0 ? C.gold : C.danger, marginTop: 6 }}>{fmt(kpi?.resultat ?? 0)}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>CA − Dépenses</div>
            </Card>
          </div>

          {/* KPIs opérationnels */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 20 }}>
            {/* <StatCard label="Dépenses"           value={fmt(kpi?.depenses_total ?? 0)} icon="💸" color={C.danger} sub={`${kpi?.nb_depenses ?? 0} dépense(s)`}/> */}
            <StatCard label="Commandes"          value={orders?.total ?? 0} icon="📋" color={C.purple}  sub={`${orders?.livrees ?? 0} livrée(s)`}/>
            <StatCard label="Taux d'annulation"  value={`${orders?.taux_annulation ?? 0}%`} icon="✖" color={C.danger} sub={`${orders?.annulees ?? 0} annulée(s)`}/>
            <StatCard label="Tables servies"     value={kpi?.nb_tables_servies ?? 0} icon="🍽" color={C.gold} sub={`Occupation ${kpi?.taux_occupation ?? 0}%`}/>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 26 }}>
            <StatCard label="Entrées de stock"   value={fmt(stock?.mouvements_periode?.entrees ?? 0)} icon="📥" color={C.success}/>
            <StatCard label="Sorties de stock"   value={fmt(stock?.mouvements_periode?.sorties ?? 0)} icon="📤" color={C.warning}/>
            <StatCard label="Produits en alerte" value={stock?.nb_en_alerte ?? 0} icon="⚠" color={C.danger} sub="État actuel"/>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
            {/* Top plats vendus sur la période */}
            <Card style={{ padding: 22 }}>
              <h3 className="serif" style={{ fontSize: 16, color: C.cream, marginBottom: 18 }}>Top plats vendus</h3>
              {topPlats.length === 0 ? <Empty icon="🍽" text="Aucune vente sur la période"/> : (
                topPlats.map((p, i) => {
                  const max = topPlats[0]?.total_qte || 1;
                  const pct = Math.round(((p.total_qte || 0) / max) * 100);
                  return (
                    <div key={p.plat__nom ?? i} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: C.mutedL }}>
                          <span style={{ color: C.muted, marginRight: 6 }}>{i + 1}.</span>{p.plat__nom}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.gold }}>
                          {p.total_qte} · {fmt(p.total_ca || 0)}
                        </span>
                      </div>
                      <div style={{ height: 4, background: C.bg4, borderRadius: 3 }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: C.gold, borderRadius: 3, transition: "width .4s" }}/>
                      </div>
                    </div>
                  );
                })
              )}
            </Card>

            {/* Produits en alerte (état courant) */}
            <Card style={{ padding: 22 }}>
              <h3 className="serif" style={{ fontSize: 16, color: C.cream, marginBottom: 18 }}>
                Produits en alerte <span style={{ fontSize: 12, color: C.muted }}>({stock?.nb_en_alerte ?? 0})</span>
              </h3>
              {alertes.length === 0 ? <Empty icon="✅" text="Aucun produit en alerte"/> : (
                alertes.slice(0, 8).map((p, i) => (
                  <div key={p.id ?? i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, padding: "7px 10px", background: C.bg3, borderRadius: 8 }}>
                    <span style={{ fontSize: 12, color: C.cream }} className="truncate">{p.nom}</span>
                    <span style={{ fontWeight: 700, color: C.danger, fontSize: 13, flexShrink: 0, marginLeft: 8 }}>
                      {p.qte} / {p.seuil}
                    </span>
                  </div>
                ))
              )}
            </Card>
          </div>

          {/* Panneau d'export — respecte la période sélectionnée */}
          <Card style={{ padding: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
              <h3 className="serif" style={{ fontSize: 16, color: C.cream }}>Exporter les données</h3>
              <span style={{ fontSize: 11, color: C.muted }}>Période : {periodLabel}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
              {[
                { label: "Commandes",  type: "orders",    icon: "📋" },
                { label: "Stock",      type: "stock",     icon: "📦" },
                { label: "Factures",   type: "invoices",  icon: "🧾" },
                { label: "Mouvements", type: "movements", icon: "📜" },
                { label: "Dépenses",   type: "depenses",  icon: "💸" },
                { label: "Synthèse KPI", type: "kpi",     icon: "📊" },
              ].map(item => (
                <Card key={item.type} style={{ padding: 16 }}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{item.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.cream, marginBottom: 12 }}>Rapport {item.label}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["CSV", "Excel", "PDF"].map(f => (
                      <Btn key={f} small variant="ghost"
                        loading={exporting === `${item.type}-${f.toLowerCase()}`}
                        onClick={() => doExport(item.type, f.toLowerCase())}>{f}</Btn>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default ReportsScreen;
